import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useIdentity, IDENTITY_PEOPLE, personLabel, isRep } from '../context/IdentityContext';
import { getPersonSkill, savePersonSkill } from '../services/api';

type SkillTab = 'skill' | 'memory';

interface RepresentativeData {
  name: string;
  skill_md: string;
  memory_md: string;
}

const MyAIRepresentatives: React.FC = () => {
  const { theme } = useTheme();
  const { currentPerson } = useIdentity();
  const [reps, setReps] = useState<RepresentativeData[]>([]);
  const [expandedRep, setExpandedRep] = useState<string | null>(null);
  const [tab, setTab] = useState<SkillTab>('skill');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadRepresentatives();
  }, [currentPerson]);

  const loadRepresentatives = async () => {
    setLoading(true);
    setError(null);
    try {
      // For now, load the current person's representative
      // In the future, this could support multiple reps
      const data = await getPersonSkill(currentPerson);
      setReps([
        {
          name: currentPerson,
          skill_md: data.skill_md,
          memory_md: data.memory_md,
        },
      ]);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load AI representatives');
      setReps([]);
    } finally {
      setLoading(false);
    }
  };

  const cardStyle: React.CSSProperties = {
    padding: '20px',
    borderRadius: '8px',
    backgroundColor: theme.surface,
    border: `1px solid ${theme.border}`,
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ color: theme.text, margin: '0 0 8px', fontSize: '28px' }}>
          My AI Representatives
        </h1>
        <p style={{ color: theme.textSecondary, margin: 0, fontSize: '15px' }}>
          This is how the right AI representative is chosen at the right time. The default rep represents you and acts on your behalf where possible.
        </p>
      </div>

      {loading && (
        <div style={{ ...cardStyle, textAlign: 'center', color: theme.textSecondary }}>
          Loading your AI representatives…
        </div>
      )}

      {error && (
        <div
          style={{
            ...cardStyle,
            backgroundColor: '#ef444422',
            border: '1px solid #ef4444',
            color: '#ef4444',
            marginBottom: '16px',
          }}
        >
          {error}
        </div>
      )}

      {!loading &&
        !error &&
        reps.map((rep) => (
          <AIRepresentativeCard
            key={rep.name}
            rep={rep}
            expanded={expandedRep === rep.name}
            onExpandedChange={(expanded) => setExpandedRep(expanded ? rep.name : null)}
            cardStyle={cardStyle}
            theme={theme}
            onDataRefresh={loadRepresentatives}
          />
        ))}

      {!loading && !error && reps.length === 0 && (
        <div
          style={{
            ...cardStyle,
            textAlign: 'center',
            color: theme.textSecondary,
          }}
        >
          No AI representatives found.
        </div>
      )}
    </div>
  );
};

const AIRepresentativeCard: React.FC<{
  rep: RepresentativeData;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  cardStyle: React.CSSProperties;
  theme: any;
  onDataRefresh: () => void;
}> = ({ rep, expanded, onExpandedChange, cardStyle, theme, onDataRefresh }) => {
  const [tab, setTab] = useState<SkillTab>('skill');
  const [skillMd, setSkillMd] = useState(rep.skill_md);
  const [memoryMd, setMemoryMd] = useState(rep.memory_md);
  const [savingSkill, setSavingSkill] = useState(false);
  const [savingMemory, setSavingMemory] = useState(false);
  const [feedback, setFeedback] = useState<{
    file: SkillTab;
    kind: 'ok' | 'error';
    msg: string;
  } | null>(null);

  useEffect(() => {
    setSkillMd(rep.skill_md);
    setMemoryMd(rep.memory_md);
  }, [rep]);

  const save = async (file: SkillTab) => {
    const setSaving = file === 'skill' ? setSavingSkill : setSavingMemory;
    setSaving(true);
    setFeedback(null);
    try {
      const payload =
        file === 'skill' ? { skill_md: skillMd } : { memory_md: memoryMd };
      const saved = await savePersonSkill(rep.name, payload);
      setSkillMd(saved.skill_md);
      setMemoryMd(saved.memory_md);
      setFeedback({ file, kind: 'ok', msg: 'Saved.' });
    } catch (err: any) {
      setFeedback({
        file,
        kind: 'error',
        msg: err.response?.data?.detail || 'Save failed. Please try again.',
      });
    } finally {
      setSaving(false);
    }
  };

  const tabButton = (id: SkillTab, label: string) => {
    const active = tab === id;
    return (
      <button
        type="button"
        onClick={() => {
          setTab(id);
          setFeedback(null);
        }}
        style={{
          padding: '8px 14px',
          borderRadius: '6px 6px 0 0',
          border: `1px solid ${theme.border}`,
          borderBottom: active ? `1px solid ${theme.surface}` : `1px solid ${theme.border}`,
          backgroundColor: active ? theme.surface : theme.background,
          color: active ? theme.text : theme.textSecondary,
          fontWeight: active ? 600 : 400,
          fontSize: '13px',
          cursor: 'pointer',
          marginRight: '4px',
        }}
      >
        {label}
      </button>
    );
  };

  const textareaStyle: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    padding: '12px',
    borderRadius: '0 6px 6px 6px',
    border: `1px solid ${theme.border}`,
    backgroundColor: theme.background,
    color: theme.text,
    fontSize: '13px',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
    lineHeight: 1.5,
    minHeight: '320px',
    resize: 'vertical',
  };

  const value = tab === 'skill' ? skillMd : memoryMd;
  const onChange = tab === 'skill' ? setSkillMd : setMemoryMd;
  const saving = tab === 'skill' ? savingSkill : savingMemory;

  return (
    <div
      style={{
        ...cardStyle,
        marginBottom: '16px',
        borderLeft: `4px solid ${theme.primary}`,
      }}
    >
      <button
        type="button"
        onClick={() => onExpandedChange(!expanded)}
        aria-expanded={expanded}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'baseline',
          gap: '10px',
          background: 'none',
          border: 'none',
          padding: 0,
          margin: 0,
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <span style={{ color: theme.textSecondary, fontSize: '14px', lineHeight: 1 }}>
          {expanded ? '▾' : '▸'}
        </span>
        <span style={{ color: theme.text, margin: 0, fontSize: '16px', fontWeight: 500 }}>
          {rep.name}
          <span style={{ color: theme.textSecondary, fontSize: '13px', fontWeight: 400, marginLeft: '12px' }}>
            {expanded ? '(close)' : '(edit)'}
          </span>
        </span>
      </button>

      {!expanded ? null : (
        <div style={{ marginTop: '14px' }}>
          <div style={{ display: 'flex' }}>
            {tabButton('skill', 'Behavior')}
            {tabButton('memory', 'Learned preferences')}
          </div>
          <textarea
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
              setFeedback(null);
            }}
            spellCheck={false}
            style={textareaStyle}
            aria-label={
              tab === 'skill' ? 'Behavior editor' : 'Learned preferences editor'
            }
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '10px' }}>
            <button
              type="button"
              onClick={() => save(tab)}
              disabled={saving}
              style={{
                padding: '8px 18px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: theme.primary,
                color: '#fff',
                fontSize: '14px',
                fontWeight: 600,
                cursor: saving ? 'default' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving
                ? 'Saving…'
                : `Save ${tab === 'skill' ? 'Behavior' : 'Preferences'}`}
            </button>
            {feedback && feedback.file === tab && (
              <span
                style={{
                  fontSize: '13px',
                  color: feedback.kind === 'ok' ? '#10b981' : '#ef4444',
                }}
              >
                {feedback.msg}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAIRepresentatives;
